/**
 * Revenue Stats month grid (WSRADM-0022 / WSRADM-0029 / WSRADM-0034).
 *
 * Dimension and currency live in `?dimension=` / `?currency=` (never useState).
 * Columns after Month total come from the envelope `familyColumns`. `arm` /
 * `average` / `totalRevenue` are rendered as sent — never divided, never summed.
 * Hidden columns live in `?hide=` + the `wsr_admin_views` cookie (WSRADM-0051);
 * hiding never produces a "visible total".
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  fetchRevenueMonths,
  isCurrencyNotFound,
  isDimensionNotFound,
  rebuildReports,
} from '@/api/reports/reportsApi';
import { useAuth } from '@/auth/AuthContext';
import { canRebuildReports } from '@/auth/reportAccess';
import { ColumnChooser, HiddenHint } from '@/components/ColumnChooser';
import { CompareToggle, useComparePref } from '@/components/CompareSupport';
import { familyColorOrNeutral } from '@/components/familyPalette';
import { ReportFreshness } from '@/components/ReportFreshness';
import { RevenueMonthsTable } from '@/components/RevenueMonthsTable';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { familyViewKey } from '@/config/viewPrefs';
import { useHiddenKeys } from '@/hooks/useHiddenKeys';
import { useResource } from '@/hooks/useResource';
import { RevenueByFamilyCharts } from './RevenueByFamilyCharts';

const PAGE_SIZE_DEFAULT = 24;

export function RevenueStatsPage() {
  const { t } = useCopy();
  const { operator } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const dimension = searchParams.get('dimension') ?? '';
  const currency = searchParams.get('currency') ?? '';
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildNotice, setRebuildNotice] = useState<'ok' | 'error' | null>(null);
  const [rebuildError, setRebuildError] = useState<unknown>(null);
  const [compareMode, setCompareMode] = useComparePref('revenueCompare');
  // Keep the last notice kind so the snackbar content does not flip to the success
  // copy while an error is transitioning out after being dismissed.
  const [lastNotice, setLastNotice] = useState<'ok' | 'error'>('ok');
  if (rebuildNotice !== null && rebuildNotice !== lastNotice) setLastNotice(rebuildNotice);
  const shownNotice = rebuildNotice ?? lastNotice;

  const resource = useResource(
    () => fetchRevenueMonths({ currency, dimension, page, size }),
    [currency, dimension, page, size],
  );

  const canRebuild = canRebuildReports(operator?.roles);
  const title = t('page.revenueStats.title', 'Revenue Stats');
  useDocumentTitle(title);
  const responseCurrency = resource.data?.currency ?? '';
  const currencies = resource.data?.currencies ?? [];
  const dimensions = resource.data?.dimensions ?? [];
  const echoed = resource.data?.dimension ?? null;
  const echoedName = dimensions.find((d) => d.slug === echoed)?.displayName ?? echoed ?? '';
  const transitional = resource.data?.transitional === true;
  const familyColumns = resource.data?.familyColumns ?? [];
  const hasMembershipColumn = familyColumns.some((column) => column.slug === 'membership');
  const familiesGroup = t('views.groupFamilies', 'Families');
  const columnOptions = [
    { key: 'monthTotal', label: t('reports.revenue.columns.monthTotal', 'Month total') },
    ...familyColumns.map((column) => {
      const palette = familyColorOrNeutral(column.color);
      return {
        key: familyViewKey(column.slug),
        label: column.displayName,
        group: familiesGroup,
        adornment: palette ? (
          <Box
            component="span"
            aria-hidden
            sx={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '2px',
              mr: 1,
              flexShrink: 0,
              bgcolor: palette.bg,
            }}
          />
        ) : undefined,
      };
    }),
    { key: 'overlap', label: t('reports.revenue.columns.overlap', 'Overlap') },
  ];
  const views = useHiddenKeys({
    param: 'hide',
    prefsKey: 'revenue',
    available: columnOptions.map((option) => option.key),
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  useEffect(() => {
    if (resource.status !== 'error') return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (isDimensionNotFound(resource.error) && dimension !== '') {
      next.delete('dimension');
      changed = true;
    } else if (isCurrencyNotFound(resource.error) && currency !== '') {
      next.delete('currency');
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
  }, [resource.status, resource.error, dimension, currency, searchParams, setSearchParams]);

  const handleRebuild = () => {
    setRebuilding(true);
    void rebuildReports()
      .then(() => setRebuildNotice('ok'))
      .catch((err: unknown) => {
        setRebuildError(err);
        setRebuildNotice('error');
      })
      .finally(() => setRebuilding(false));
  };

  const neverComputed = resource.status === 'ready' && resource.data?.neverComputed === true;
  const showOverlapNote =
    resource.status === 'ready' &&
    (resource.data?.content ?? []).some((row) => row.overlapLineCount > 0);

  return (
    <>
      <Typography variant="h1" sx={{ mb: 2.5 }}>
        {title}
      </Typography>

      {resource.data ? (
        <ReportFreshness
          asOf={resource.data.asOf}
          stale={resource.data.stale}
          neverComputed={resource.data.neverComputed}
          timezone={resource.data.timezone}
          canRebuild={canRebuild}
          onRebuild={handleRebuild}
          rebuilding={rebuilding}
        />
      ) : null}

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        {dimensions.length > 0 ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label={t('reports.revenue.dimension', 'Dimension')}
            value={echoed ?? ''}
            onChange={(_event, value: string | null) => {
              if (value === null) return;
              setParam('dimension', value);
            }}
          >
            {dimensions.map((option) => (
              <ToggleButton key={option.slug} value={option.slug}>
                {option.displayName}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        ) : null}

        {currencies.length > 1 ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label={t('reports.revenue.currency', 'Currency')}
            value={responseCurrency}
            onChange={(_event, value: string | null) => {
              if (value === null) return;
              setParam('currency', value);
            }}
          >
            {currencies.map((code) => (
              <ToggleButton key={code} value={code}>
                {code}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        ) : null}

        {resource.status === 'ready' && !neverComputed ? (
          <>
            <CompareToggle value={compareMode} onChange={setCompareMode} />
            <ColumnChooser
              options={columnOptions}
              hidden={views.hidden}
              onToggle={views.toggle}
              onShowAll={views.showAll}
            />
          </>
        ) : null}
      </Stack>

      {neverComputed ? null : (
        <>
          {transitional ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                'reports.revenue.transitional',
                'Awaiting first rebuild for this dimension — the cells shown are from before it existed.',
              )}
            </Alert>
          ) : null}
          <RevenueMonthsTable
            rows={resource.data?.content ?? []}
            familyColumns={familyColumns}
            currency={responseCurrency}
            dimension={echoed}
            transitional={transitional}
            hasMembershipColumn={hasMembershipColumn}
            hidden={views.hidden}
            compare={compareMode}
            status={resource.status}
            error={resource.error}
            totalElements={resource.data?.totalElements ?? 0}
            page={page}
            size={size}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
          {resource.status === 'ready' ? (
            <HiddenHint count={views.hiddenCount} kind="columns" onShowAll={views.showAll} />
          ) : null}
          {showOverlapNote ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t(
                'reports.revenue.overlapNote',
                'Family columns overlap and do not sum to the month total; a line can be counted in more than one family.',
              )}
            </Typography>
          ) : null}
          <RevenueByFamilyCharts
            rows={resource.data?.content ?? []}
            familyColumns={familyColumns}
            hidden={views.hidden}
            currency={responseCurrency}
            dimensionName={echoedName}
          />
        </>
      )}

      <Paper variant="outlined" sx={{ mt: 3, p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          {t('reports.revenue.benchmark.title', 'Typical ARM Ranges')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('reports.revenue.benchmark.strugglingLabel', 'Struggling')}{' '}
          {t('reports.revenue.benchmark.strugglingRange', '$90–120')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('reports.revenue.benchmark.averageLabel', 'Average')}{' '}
          {t('reports.revenue.benchmark.averageRange', '$140–180')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('reports.revenue.benchmark.healthyLabel', 'Healthy')}{' '}
          {t('reports.revenue.benchmark.healthyRange', '$200+')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('reports.revenue.benchmark.topLabel', 'Top performers')}{' '}
          {t('reports.revenue.benchmark.topRange', '$250–350')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'reports.revenue.benchmark.usdNote',
            'These benchmarks are USD reference figures.',
          )}
        </Typography>
      </Paper>

      <Snackbar
        open={rebuildNotice !== null}
        autoHideDuration={rebuildNotice === 'error' ? null : 6000}
        onClose={(_event, reason) => {
          // A failure stays until dismissed — a click elsewhere must not hide it.
          if (reason === 'clickaway' && rebuildNotice === 'error') return;
          setRebuildNotice(null);
        }}
      >
        <Alert
          severity={shownNotice === 'error' ? 'error' : 'success'}
          onClose={() => setRebuildNotice(null)}
        >
          {shownNotice === 'error' ? (
            <>
              {t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              <ErrorDetails
                error={rebuildError}
                headline={t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              />
            </>
          ) : (
            t(
              'reports.freshness.rebuildQueued',
              'Rebuild queued. Numbers will refresh when the rollup finishes.',
            )
          )}
        </Alert>
      </Snackbar>
    </>
  );
}
